// Spooky lofi (docs/AUDIO_DESIGN.md §12): cozy-haunted, not horror. An old music box / celesta plays minor chords as
// broken-chord tunes (the chromatic lament bass, the minor line cliché, diminished 7ths, the Neapolitan ♭II, tritone subs;
// major keys borrow from the parallel minor or centre on vi), a soft upright bass creeps down chromatically, a slow kit of a
// deep kick, finger snaps on the backbeat and a tick-tock clock, and a theremin (or a bowed saw) glides into every note.
// Instruments are in spooky-voices.js.
import { rng } from '../../engine/core.js';
import { QUALITIES, bassNote } from '../harmony.js';
import { createCelesta, createUpright, createSpookKit, createTheremin } from './spooky-voices.js';

const mod12 = n => ((n % 12) + 12) % 12;
const avg = a => a.reduce((s, x) => s + x, 0) / a.length;
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};

/* ---------------- voicing: a low tooth on the root + a music-box right hand ---------------- */
// Right-hand tones per quality (semitones over the root; the root itself is the low tooth's): ♭3 5 R on a minor triad, 3 5 R
// on a major one, 3 ♯5 R on the augmented, ♭3 5 7 (9) on m(maj7), ♭3 ♭5 6 on dim7 (the tooth makes it a full dim7), ♭3 ♭5 ♭7
// on m7♭5, 9 11 13 on spkmb7 (the i chord over its ♭7: the lament's second step), 3 ♯5 ♭7 on 7♯5, 3 ♭7 ♭9 on 7♭9, plain
// 3 5 7 / ♭3 5 ♭7 on sevenths (the 9th on 9ths), 3 6 9 on 6/9. Anything else: its tones minus the root, 5th dropped first.
const RH = {
  spkm: [3, 7, 12], spkmaj: [4, 7, 12], spkaug: [4, 8, 12], spkmmaj7: [3, 7, 11], spkmmaj9: [3, 7, 11, 14], spkmb7: [2, 5, 9],
  spk7s5: [4, 8, 10], m7: [3, 7, 10], m9: [3, 7, 10, 14], m6: [3, 7, 9], m7b5: [3, 6, 10], dim7: [3, 6, 9], madd9: [3, 7, 14],
  maj7: [4, 7, 11], maj9: [4, 7, 11, 14], 69: [4, 9, 14], 6: [4, 7, 9], add9: [4, 7, 14], 7: [4, 7, 10], '7b9': [4, 10, 13],
};
const rhTones = q => {
  if (RH[q]) return RH[q];
  let iv = [...new Set((QUALITIES[q] || QUALITIES.m9).filter(i => i % 12))];
  if (iv.length > 4) iv = iv.filter(i => i !== 7).slice(0, 4);
  return iv.length ? iv : [3, 7];
};
const perms = n => (n <= 1 ? [[0]] : perms(n - 1).flatMap(p => Array.from({ length: n }, (_, k) => [...p.slice(0, k), n - 1, ...p.slice(k)])));
const PERMS = [null, perms(1), perms(2), perms(3), perms(4)];
const CANDS = new Map();
/** Right-hand candidates: each order of the tones stacked upwards from a lowest note in MIDI 57–69, kept when the top is
 *  67–81 and the span 5–16, with neighbours ≥ a whole step apart (≥ a minor 3rd below MIDI 60) and no minor 9th. The first
 *  chord's window is widened by the track's register (e.g. +5: lowest 57–74, top 67–86). Cached. */
function rhCandidates(pcs, reg = 0) {
  const id = `${pcs.join(',')}:${reg}`, lo0 = 57 + Math.min(0, reg), lo1 = 69 + Math.max(0, reg), t0 = 67 + Math.min(0, reg), t1 = 81 + Math.max(0, reg);
  if (CANDS.has(id)) return CANDS.get(id);
  const out = [];
  for (const order of PERMS[pcs.length]) for (let lo = lo0; lo <= lo1; lo++) {
    if (mod12(lo) !== pcs[order[0]]) continue;
    const v = [lo];
    for (let k = 1; k < order.length; k++) { let m = v[k - 1] + 1; while (mod12(m) !== pcs[order[k]]) m++; v.push(m); }
    const top = v[v.length - 1], span = top - lo;
    if (top < t0 || top > t1 || span < 5 || span > 16) continue;
    let ok = true;
    for (let k = 1; k < v.length && ok; k++) if (v[k] - v[k - 1] < 2 || (v[k - 1] < 60 && v[k] - v[k - 1] < 3)) ok = false;
    for (let a = 0; a < v.length && ok; a++) for (let c = a + 1; c < v.length; c++) if (v[c] - v[a] === 13) ok = false;
    if (ok && !out.some(w => w.join() === v.join())) out.push(v);
  }
  CANDS.set(id, out);
  return out;
}
/** Cost against the previous right hand (voices paired from the top): Σ|Δ| + 1.5·max(0, |Δtop| − 2) (+4 past a 4th) − .6 per
 *  common tone, + .35·|mean − 67| + .6·max(0, top − 78) + .12·|span − 10| + 2 per semitone the right hand sits closer than a
 *  5th over the low tooth. The first chord: the register terms and .3·|top − 74|, all moved by the track's register (−5..+5,
 *  plan.register), so consecutive tracks don't open on the same top note. */
function rhCost(v, prev, lh, reg = 0) {
  const top = v[v.length - 1], home = prev ? 0 : reg;
  let c = 0.35 * Math.abs(avg(v) - 67 - home) + 0.6 * Math.max(0, top - 78 - home) + 0.12 * Math.abs(top - v[0] - 10) + 2 * Math.max(0, lh + 7 - v[0]);
  if (!prev) return c + 0.3 * Math.abs(top - 74 - reg);
  for (let i = 1; i <= Math.min(v.length, prev.length); i++) c += Math.abs(v[v.length - i] - prev[prev.length - i]);
  const dt = Math.abs(top - prev[prev.length - 1]);
  c += 1.5 * Math.max(0, dt - 2) + (dt > 5 ? 4 : 0);
  for (const m of v) if (prev.includes(m)) c -= 0.6;
  return c;
}
/** [low root, ...right hand low → high]: the low tooth (the root at MIDI 40–55) and the right hand are chosen together, the
 *  tooth paying .6 per semitone it moves and .3 per semitone from 48 on top of rhCost (so it stays a 5th or more under the
 *  right hand and a lament's root line can step down in every key). The same chord again keeps its voicing (re-strikes and
 *  2-bar chords). The first chord aims at the home register moved by plan.register (the tooth from 48 + register too). */
function voiceSpooky(sp, prev, chord, plan) {
  const root = mod12(plan.key.tonic + chord.root), pcs = rhTones(chord.q).map(i => mod12(root + i)), set = new Set(pcs);
  if (prev && mod12(prev[0]) === root && prev.length === pcs.length + 1 && prev.slice(1).every(m => set.has(mod12(m)))
    && pcs.every(pc => prev.slice(1).some(m => mod12(m) === pc))) return prev.slice();
  const reg = plan.register || 0, from = prev ? prev[0] : 48 + reg, pr = prev ? prev.slice(1) : null, cands = rhCandidates(pcs, prev ? 0 : reg);
  let best = null, bc = Infinity;
  for (let lh = 40 + mod12(root - 40); lh <= 55; lh += 12) {
    const k = 0.6 * Math.abs(lh - from) + 0.3 * Math.abs(lh - 48);
    for (const v of cands) { const c = k + rhCost(v, pr, lh, reg); if (c < bc) { bc = c; best = [lh, ...v]; } }
  }
  if (!best) {
    best = [40 + mod12(root - 40)];
    for (const pc of pcs) { let m = 60 + mod12(pc - 60); while (best.length > 1 && m <= best[best.length - 1]) m += 12; best.push(m); }
  }
  return best;
}

/* ---------------- comping: music-box figures; every note rings to the bar's end (a bell decays on its own) ---------------- */
// cells of [step, length, velocity, pick]; pick indices into the voicing: 0 = the low root, 1.. the right hand, −1 the top
const cell = (s, v = 1, pick = null) => (pick ? [s, 16 - s, v, pick] : [s, 16 - s, v]);
const COMPS = {
  hold: [cell(0)],                                                                                       // a rolled celesta chord
  box: [cell(0, 1, [0]), cell(2, 0.62, [1]), cell(4, 0.68, [2]), cell(6, 0.74, [-1]), cell(8, 0.8, [0]), cell(10, 0.62, [2]),
    cell(12, 0.68, [-1]), cell(14, 0.58, [1])],                                                          // the broken chord in 8ths
  lull: [cell(0, 1, [0]), cell(4, 0.7, [1, 2]), cell(8, 0.78, [0, -1]), cell(12, 0.66, [1, 2])],          // a lullaby's rocking
  drip: [cell(0, 1, [0, -1]), cell(6, 0.58, [-2]), cell(11, 0.52, [1])],                                  // root + top, then drops
  twinkle: [cell(0), cell(6, 0.55, [-1]), cell(10, 0.5, [-2]), cell(14, 0.5, [-1])],                       // held, the top twinkling
  carousel: [cell(0, 1, [0]), cell(3, 0.6, [1]), cell(4, 0.66, [2]), cell(6, 0.72, [-1]), cell(8, 0.82, [0]), cell(11, 0.6, [1]),
    cell(12, 0.66, [2]), cell(14, 0.72, [-1])],                                                          // a dotted merry-go-round
};

/* ---------------- drums: a slow boom-bap under a ticking clock ---------------- */
// K deep kick · C finger snap · H the clock (x tick, o tock) · X a bone rattle · R a knock on the door; s = soft (×.55)
const GRIDS = {
  //         0123456789abcdef
  creep: { K: 'x.........x.....', C: '....x.......x...', H: 'x...o...x...o...' },                                  // boom-bap
  tiptoe: { K: 'x......x..x.....', C: '....x.......x...', H: 'x...o...x...o...', X: '..s.......s.....' },
  candle: { K: 'x.....x.........', C: '........x.......', H: 'x...o...x...o...' },                                 // halftime
  hush: { K: 'x...............', C: '........x.......', H: 'x...o...x...o...' },                                   // one kick
  prowl: { K: 'x.....x...x.....', C: '....x.......x..s', H: 'x...o...x...o...', X: '......s.......s.' },
  skel: { K: 'x..x......x.....', C: '....x.......x...', H: 'x.s.o.s.x.s.o.s.', X: '..s...s...s...s.' },               // a skeleton's walk
  break: { H: 'x...o...x...o...' },                                                                                 // the clock alone
};
const GROOVES = {
  chill: { hush: 1.6, candle: 1.4, creep: 1, tiptoe: 0.4 },
  slow: { creep: 1.4, candle: 1, tiptoe: 1, prowl: 0.6, hush: 0.5 },
  fast: { tiptoe: 1.2, creep: 1, prowl: 1, skel: 0.5 },
  upbeat: { prowl: 1.2, tiptoe: 1, skel: 1, creep: 0.8 },
};

/* ---------------- plan: timbres, the box's tune, the ghost's motif ---------------- */
// 2-bar theremin motifs [steps, durations] (16ths; slow, the last note long) and contours in scale steps (wide leaps the
// portamento turns into swoops)
const GCELLS = [
  [[0, 8], [8, 20]], [[0, 12], [12, 18]], [[0, 6, 12], [6, 6, 18]], [[2, 8, 16], [6, 8, 14]], [[0, 4, 8, 16], [4, 4, 8, 14]],
  [[0, 10, 16], [10, 6, 14]], [[0, 3, 8, 16], [3, 5, 8, 14]],
];
const GSHAPES = {
  2: [[0, 3], [0, -2], [0, 2], [0, 4], [0, -3]],
  3: [[0, 3, 2], [0, 2, -1], [0, -2, 1], [0, 4, 3], [0, 1, -2], [0, -3, -1]],
  4: [[0, 2, 4, 3], [0, 3, 2, 0], [0, -1, 2, 1], [0, 2, 1, -1], [0, -2, -1, 1]],
};
function ghostMotif(r, key) {
  const [steps, durs] = GCELLS[Math.floor(r() * GCELLS.length)], shapes = GSHAPES[steps.length];
  const contour = shapes[Math.floor(r() * shapes.length)];
  return { steps, durs, contour, vel: steps.map((s, i) => (i ? +(0.6 + 0.08 * r()).toFixed(2) : 0.72)), mode: key.mode };
}
function plan(p, { r, E }) {
  const rr = (a, b, d = 2) => +(a + r() * (b - a)).toFixed(d);
  p.spooky = {
    detune: rr(1.5, 4, 1), twin: rr(4, 9, 1) * (r() < 0.5 ? -1 : 1), strike: rr(0.8, 1.2), click: rr(0.7, 1.3),
    flutter: rr(...E.flutter, 1), rate: rr(1.1, 1.8), roll: rr(...E.roll), wind: r() < E.wind, bass: pickW(r, E.bassPat), walk: rr(...E.walk),
  };
  // the box's tune: one of its figures a track leans on (×2.5), like a cylinder that plays the same pattern
  const { box, lull, twinkle, carousel } = p.ep.comp, tune = pickW(r, { box, lull, twinkle, carousel });
  p.ep.comp = { ...p.ep.comp, [tune]: +(p.ep.comp[tune] * 2.5).toFixed(2) };
  p.drums.kit = {
    kickF0: rr(100, 115, 1), kickF1: rr(44, 50, 1), kickDecay: rr(0.16, 0.22, 3), snapBP: rr(2200, 2900, 0), snapDecay: rr(0.022, 0.034, 3),
    tick: rr(1900, 2500, 0), tock: rr(1250, 1600, 0), lp: p.drums.kit.lp,
  };
  if (p.lead) {
    const saw = p.lead.timbre === 'vibes';
    Object.assign(p.spooky, saw ? { vib: rr(18, 28, 1), vibRate: rr(3.8, 4.6) } : { vib: rr(26, 42, 1), vibRate: rr(4.8, 5.8) },
      { glide: rr(0.06, 0.12, 3), swoop: rr(0.3, 0.6) });
    p.lead.timbre = saw ? 'saw' : 'theremin';
    p.lead.motif = ghostMotif(r, p.key);
  }
}

/* ---------------- bass: a soft upright, creeping down chromatically ---------------- */
const BASS_ALT = { hold: 1, two: 1, walk: 1 };
/** One pattern per track (another on 20 % of bars): hold (the root to the chord's end) · two (root on 1, the 5th on 3) · walk
 *  (the same, creeping more). A bar whose next root lies 2–4 semitones below walks there chromatically on its last quarters
 *  (p plan.spooky.walk in walk, half of it in two), else a new root is approached from a semitone above on step 14 (p .45 in
 *  walk, .2 in two). The intro's last bar, the break and the outro hold whole roots; the final one rings to the end. */
function bass({ plan: p, st, b, mode, chords, nextChords, stop }) {
  const K = p.key, S = p.spooky, out = [], end = stop ?? 16, pc = c => mod12(K.tonic + c.root);
  const pat = mode !== 'kick' ? 'hold' : st.rS() < 0.2 ? pickW(st.rS, BASS_ALT) : S.bass;
  chords.forEach((c, k) => {
    const s = c.start * 4;
    if (s >= end) return;
    const m = bassNote(pc(c), st.prevBass);
    st.prevBass = m;
    if (c.final) { out.push({ s, midi: m, vel: 0.78, dur: (b.n - 2) * 16 }); return; }
    const e = Math.min(end, s + c.beats * 4), vel = c.onset === false ? 0.66 : 0.8;
    const nx = pat !== 'hold' && k === chords.length - 1 && e === 16 && nextChords ? nextChords[0] : null;
    const tgt = nx ? bassNote(pc(nx), m) : null, gap = nx ? m - tgt : 0;
    if (nx && gap >= 2 && gap <= 4 && st.rS() < S.walk * (pat === 'walk' ? 1 : 0.5)) {   // creep down: m, m − 1, … into the next root
      const first = 16 - 4 * (gap - 1);
      if (first >= s + 4) {
        out.push({ s, midi: m, vel, dur: first - s - 0.4 });
        for (let j = 1; j < gap; j++) out.push({ s: first + 4 * (j - 1), midi: m - j, vel: 0.62, dur: 3.6 });
        return;
      }
    }
    const appr = nx && pc(nx) !== pc(c) && st.rS() < (pat === 'walk' ? 0.45 : 0.2), cut = appr ? Math.min(e, 14) : e;
    const iv = QUALITIES[c.q] || [0, 7], d5 = iv.includes(7) ? 7 : iv.includes(6) ? 6 : iv.includes(8) ? 8 : 0;
    const fifth = m + d5 <= 47 ? m + d5 : m + d5 - 12;
    if (pat === 'hold' || cut - s < 12) out.push({ s, midi: m, vel, dur: cut - s - 0.3 });
    else { out.push({ s, midi: m, vel, dur: 7.4 }); out.push({ s: s + 8, midi: fifth, vel: 0.68, dur: cut - s - 8.3 }); }
    if (appr) out.push({ s: 14, midi: tgt + 1, vel: 0.6, dur: 1.8 });
  });
  return out;
}

export default {
  id: 'spooky', name: 'Spooky', desc: 'A haunted music box, a theremin and finger snaps by candlelight',
  //            chill                        balanced                     upbeat
  vary: 'colour',
  energy: {
    stretch:    [0.55,                       0.4,                         0.25],             // odds of 2-bar chords
    bpm:        [[58, 66],                   [64, 74],                    [70, 82]],         // 58: the 54-bar forms stay ≤ 240 s
    swing:      [[56, 62],                   [55, 60],                    [54, 58]],
    snareLag:   [[0.012, 0.022],             [0.008, 0.018],              [0.005, 0.012]],   // the snaps sit back
    groove:     [[GROOVES.chill, GROOVES.chill], [GROOVES.slow, GROOVES.fast], [GROOVES.upbeat, GROOVES.upbeat]],
    drumLevel:  [0.7,                        0.76,                        0.82],
    kitLp:      [[3600, 5000],               [4200, 5800],                [4800, 6600]],
    epLp:       [[3400, 4600],               [3800, 5200],                [4200, 5800]],     // the music box's bus low-pass
    strum:      [[0.02, 0.045],              [0.015, 0.035],              [0.01, 0.025]],
    epVel:      [[0.5, 0.6],                 [0.52, 0.62],                [0.55, 0.65]],
    comp: [
      { hold: [0.8, 1.4], box: [0.2, 0.6], lull: [0.3, 0.8], drip: [0.4, 1], twinkle: [0.2, 0.6], carousel: [0, 0] },
      { hold: [0.3, 0.8], box: [0.5, 1.1], lull: [0.4, 0.9], drip: [0.2, 0.6], twinkle: [0.3, 0.7], carousel: [0, 0.3] },
      { hold: [0.1, 0.4], box: [0.6, 1.2], lull: [0.3, 0.8], drip: [0.1, 0.4], twinkle: [0.3, 0.7], carousel: [0.3, 0.8] },
    ],
    push:       [0.04,                       0.08,                        0.14],
    lead:       [0.6,                        0.7,                         0.78],
    softLead:   [0.72,                       0.66,                        0.6],              // odds of the theremin over the saw
    tone:       [[4200, 6200],               [5000, 7400],                [5800, 8400]],
    wowCents:   [[7, 12],                    [6, 10],                     [5, 9]],
    dust:       [[1, 1.7],                   [0.9, 1.5],                  [0.7, 1.3]],
    introHats:  [0.35,                       0.5,                         0.6],              // the clock ticks in the intro
    leadFrom:   [2,                          2,                           1],
    hatsFirst:  [0.4,                        0.3,                         0.2],              // … and alone in B's first bar
    breakDrums: [0.5,                        0.65,                        0.8],              // the break: the clock alone
    breakLead:  [0.35,                       0.45,                        0.55],
    roll:       [[0.3, 0.5],                 [0.2, 0.4],                  [0.1, 0.3]],       // odds a block chord is rolled
    flutter:    [[3, 5],                     [2.5, 4.5],                  [2, 4]],           // ¢ of the box's governor flutter
    wind:       [0.45,                       0.35,                        0.25],             // odds the key is wound first
    walk:       [[0.4, 0.7],                 [0.35, 0.65],                [0.3, 0.6]],       // odds of a chromatic creep down
    bassPat:    [{ hold: 1, two: 0.35, walk: 0.5 }, { hold: 0.5, two: 0.8, walk: 1 }, { hold: 0.25, two: 1, walk: 1 }],
  },
  qualities: {
    spkm: [0, 3, 7], spkmaj: [0, 4, 7], spkaug: [0, 4, 8], spkmmaj7: [0, 3, 7, 11], spkmmaj9: [0, 3, 7, 11, 14],
    spkmb7: [0, 2, 5, 9], spk7s5: [0, 4, 8, 10],
  },
  progressions: {
    minor: {
      h1: [[0, 'm9', 4], [10, 'spkmb7', 4], [9, 'm7b5', 4], [8, 'maj7', 4]],                  // lament: i · i/♭7 · i/6 · ♭VImaj7
      h2: [[0, 'spkm', 4], [0, 'spkmmaj7', 4], [0, 'm7', 4], [0, 'm6', 4]],                  // line cliché i · i(maj7) · i7 · i6
      h3: [[0, 'm9', 4], [11, 'dim7', 4], [0, 'm6', 4], [7, '7b9', 4]],                      // i · vii°7 · i6 · V7♭9
      h4: [[0, 'm9', 4], [5, 'm6', 4], [1, 'maj7', 4], [7, 'spk7s5', 4]],                    // i · iv6 · ♭IImaj7 (Neapolitan) · V7♯5
      h5: [[2, 'm7b5', 4], [1, '7', 4], [0, 'spkmmaj9', 4], [0, 'm6', 4]],                   // iiø7 · ♭II7 (tritone sub) · i(maj9) · i6
      h6: [[0, 'm9', 4], [8, 'maj7', 4], [6, 'dim7', 4], [7, '7b9', 4]],                     // i · ♭VImaj7 · ♯iv°7 · V7♭9
      h7: [[0, 'spkm', 4], [3, 'spkaug', 4], [8, 'maj7', 4], [7, 'spk7s5', 4]],              // i · ♭III+ · ♭VImaj7 · V7♯5
    },
    major: {
      H1: [[9, 'm9', 4], [7, 'spkmb7', 4], [6, 'm7b5', 4], [5, 'maj7', 4]],                  // the lament on vi
      H2: [[0, 'spkmaj', 4], [0, 'spkaug', 4], [0, '6', 4], [5, 'm6', 4]],                   // I · I+ · I6 · iv6
      H3: [[9, 'spkm', 4], [9, 'spkmmaj7', 4], [9, 'm7', 4], [9, 'm6', 4]],                  // the cliché on vi
      H4: [[0, 'maj7', 4], [1, 'dim7', 4], [2, 'm7', 4], [1, '7', 4]],                       // Imaj7 · ♯i°7 · ii7 · ♭II7
      H5: [[9, 'm9', 4], [5, 'm6', 4], [0, 'maj7', 4], [4, '7b9', 4]],                       // vi · iv6 · Imaj7 · III7♭9
      H6: [[0, 'maj7', 4], [8, 'maj7', 4], [5, 'm6', 4], [7, 'spk7s5', 4]],                  // Imaj7 · ♭VImaj7 · iv6 · V7♯5
      H7: [[9, 'm9', 4], [5, 'maj7', 4], [2, 'm7b5', 4], [4, '7b9', 4]],                     // vi · IVmaj7 · iiø7 · III7♭9
    },
  },
  turnarounds: { major: [[5, 'm6', 2], [4, '7b9', 2]], minor: [[2, 'm7b5', 2], [1, '7', 2]] },   // iv6|III7♭9 · iiø7|♭II7
  grids: GRIDS,
  fills: {
    stop: { stop: 12 },                                                                       // the band stops, the box rings on
    snaps: { roll: [[12, 0.45], [14, 0.7], [15, 0.5]], voice: 'clap' },                         // snap pickups
    knock: { roll: [[13, 0.55], [14, 0.6], [15, 0.7]], voice: 'rim' },                          // knock, knock, knock
    kick: { kicks: [14] },
  },
  bVariation: false,
  comps: COMPS,
  keysLevel: 1.4,
  plan,
  voicing: voiceSpooky,
  bass,
  voices(ac, dry, wet, p, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    return {
      ep: createCelesta(ac, dry, wet, p, { ...o, cap: ctx.cap, r: sub(), log: ctx.log, stepDur: ctx.stepDur }),
      bass: createUpright(ac, dry, o),
      kit: createSpookKit(ac, dry, wet, p.drums.kit, { ...o, r: sub() }),
      lead: p.lead ? createTheremin(ac, dry, wet, p, { ...o, stepDur: ctx.stepDur, r: sub() }) : undefined,
    };
  },
};
